import { defineStore } from 'pinia'
import { authService } from '@/app/Services'
import { identityKey, performLogin, getWalletContext } from '@/app/utils/auth'
import type { BffUserMe } from '@/types/bff'
import type { AuthSessionParams } from '@/types/auth'

type AuthStoreState = {
  accessToken: string
  signerAddress: string
  user: null | BffUserMe
  granterAddress: string
  expiresAt: null | number
  refreshTokenExpiresAt: null | number
}

const initialStateFactory = (): AuthStoreState => ({
  user: null,
  accessToken: '',
  expiresAt: null,
  signerAddress: '',
  granterAddress: '',
  refreshTokenExpiresAt: null
})

let loginPromiseKey: null | string = null
let loginPromise: null | Promise<void> = null
let refreshPromise: null | Promise<void> = null

export const useAuthStore = defineStore('auth', {
  state: (): AuthStoreState => initialStateFactory(),

  getters: {
    isAuthenticated: (state) => !!state.accessToken
  },

  actions: {
    setUser(user: null | BffUserMe) {
      this.user = user
    },

    setSession(params: AuthSessionParams) {
      this.signerAddress = params.signerAddress
      this.granterAddress = params.granterAddress
      this.expiresAt = params.expiresAt
      this.accessToken = params.accessToken
      this.refreshTokenExpiresAt = params.refreshTokenExpiresAt
    },

    setSessionTokens(params: {
      accessToken: string
      refreshTokenExpiresAt: null | number
    }) {
      this.accessToken = params.accessToken
      this.refreshTokenExpiresAt = params.refreshTokenExpiresAt
    },

    async login() {
      const context = getWalletContext()

      if (!context) {
        return
      }

      const key = identityKey(context.identity)

      if (loginPromise && loginPromiseKey === key) {
        return loginPromise.catch(() => {})
      }

      const authStore = useAuthStore()

      const promise: Promise<void> = performLogin(context)
        .then((session) => {
          if (!session) {
            return
          }

          authStore.setSession(session)
        })
        .finally(() => {
          if (loginPromise === promise) {
            loginPromise = null
            loginPromiseKey = null
          }
        })

      loginPromiseKey = key
      loginPromise = promise

      // silently fail, we don't want to disrupt the user experience in case of a failed login (e.g. due to network issues)
      return promise.catch(() => {})
    },

    async fetchUser() {
      const authStore = useAuthStore()

      try {
        const user = await authService.getMe()

        if (user === null) {
          authStore.reset()

          return
        }

        authStore.setUser(user)
      } catch {
        // silently fail, we don't want to disrupt the user experience in case of a failed fetchUser (e.g. due to network issues)
      }
    },

    async refreshSession() {
      if (refreshPromise) {
        return refreshPromise
      }

      refreshPromise = (async () => {
        const authStore = useAuthStore()
        const sharedWalletStore = useSharedWalletStore()

        const walletMismatch =
          authStore.granterAddress !== sharedWalletStore.injectiveAddress ||
          authStore.signerAddress !==
            sharedWalletStore.autoSign?.injectiveAddress

        if (authStore.accessToken && walletMismatch) {
          authStore.reset()
        }

        if (authStore.accessToken) {
          await authStore.fetchUser()
        }

        if (authStore.accessToken) {
          return
        }

        const canLogin =
          !!sharedWalletStore.autoSign && sharedWalletStore.isUserConnected

        if (!canLogin) {
          return
        }

        await authStore.login()

        if (authStore.accessToken) {
          await authStore.fetchUser()
        }
      })().finally(() => {
        refreshPromise = null
      })

      return refreshPromise
    },

    async logout() {
      const authStore = useAuthStore()

      if (authStore.accessToken) {
        // silently fail, we don't want to disrupt the user experience in case of a failed login (e.g. due to network issues)
        authService.logout().catch(() => {})
      }

      authStore.reset()
    },

    reset() {
      Object.assign(this, initialStateFactory())
    }
  }
})
